import { TRUCK_COMPLIANCE_STATUSES, TRUCK_STATUSES, type RigListItem, type RigListQuery } from '@chukua/common';
import { Tag, Typography, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import { COMPLIANCE_LABELS } from '../../../../../shared/features/compliance-documents/policies/document-status-labels';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { RigSilhouette } from '../../../components/RigSilhouette';
import { TRUCK_STATUS_COLORS, TRUCK_STATUS_LABELS } from '../../trucks/policies/truck-status-labels';
import { unitPath } from '../units.paths';
import { UnitRowActions, type UnitRowAction } from './UnitRowActions';

interface BuildUnitColumnsArgs {
  list: Pick<ListQueryControls<RigListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  canManage: boolean;
  classOptions: { text: string; value: string }[];
  onAction: (action: UnitRowAction, rig: RigListItem) => void;
}

const COMPLIANCE_FILTERS = TRUCK_COMPLIANCE_STATUSES.map((status) => ({ text: COMPLIANCE_LABELS[status].label, value: status }));
const STATUS_FILTERS = TRUCK_STATUSES.map((status) => ({ text: TRUCK_STATUS_LABELS[status], value: status }));

/** The line under a unit's name: its trailer, or why it has none. */
function subLabel(rig: RigListItem): string {
  if (rig.kind === 'coupled') return `with ${rig.trailer?.registration}`;
  if (rig.kind === 'trailer_only') return rig.trailer?.isActive === false ? 'Deactivated trailer' : 'Spare trailer';
  return rig.truck?.coupling === 'none' ? 'Rigid, tows nothing' : 'No trailer';
}

/** A unit is named by its truck's registration; a spare trailer by its own. The name links to the unit's page. */
function UnitCell({ rig }: { rig: RigListItem }) {
  const name = rig.truck?.registration ?? rig.trailer?.registration ?? '';
  return (
    <div className="flex min-w-[18rem] items-center gap-3">
      <RigSilhouette truck={rig.truck} trailer={rig.trailer} className="h-8 w-32 shrink-0" />
      <div className="flex flex-col leading-snug">
        <Link className="text-link hover:underline" to={unitPath(name)} onClick={(event) => event.stopPropagation()}>
          <Typography.Text code>{name}</Typography.Text>
        </Link>
        <span className="text-xs text-muted">{subLabel(rig)}</span>
      </div>
    </div>
  );
}

function StatusCell({ rig }: { rig: RigListItem }) {
  if (rig.truck) return <Tag color={TRUCK_STATUS_COLORS[rig.truck.status]}>{TRUCK_STATUS_LABELS[rig.truck.status]}</Tag>;
  if (rig.trailer?.isActive === false) return <Tag>Deactivated</Tag>;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Tag>Parked</Tag>
      {rig.parkedAt && <span className="text-xs text-muted">{rig.parkedAt.zoneName ?? rig.parkedAt.zoneCode}</span>}
    </div>
  );
}

function ComplianceCell({ rig }: { rig: RigListItem }) {
  const label = COMPLIANCE_LABELS[rig.compliance];
  const worse = [rig.truck, rig.trailer].find((vehicle) => vehicle && vehicle.compliance === rig.compliance && rig.compliance !== 'valid');
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Tag color={label.color}>{label.label}</Tag>
      {worse && rig.kind === 'coupled' && <span className="font-mono text-xs text-muted">{worse.registration}</span>}
    </div>
  );
}

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `rigListQuerySchema`. */
export function buildUnitColumns({ list, canManage, classOptions, onAction }: BuildUnitColumnsArgs): TableColumnsType<RigListItem> {
  return [
    { title: 'Unit', key: 'registration', sorter: true, sortOrder: list.sortOrderFor('registration'), render: (_, rig) => <UnitCell rig={rig} /> },
    {
      title: 'Make-up',
      key: 'classCode',
      filters: classOptions,
      filteredValue: list.filteredValueFor('classCode'),
      render: (_, rig) => (
        <div className="flex flex-col">
          <span className="font-mono text-xs">{[rig.truck?.classCode, rig.trailer?.typeCode].filter(Boolean).join(' + ')}</span>
          <span className="text-xs text-muted">{[rig.truck?.className, rig.trailer?.typeName].filter(Boolean).join(' · ')}</span>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      filters: STATUS_FILTERS,
      filteredValue: list.filteredValueFor('status'),
      render: (_, rig) => <StatusCell rig={rig} />,
    },
    {
      title: 'Compliance',
      key: 'compliance',
      filters: COMPLIANCE_FILTERS,
      filteredValue: list.filteredValueFor('compliance'),
      render: (_, rig) => <ComplianceCell rig={rig} />,
    },
    ...(canManage ? [{ title: '', key: 'actions', render: (_: unknown, rig: RigListItem) => <UnitRowActions rig={rig} onAction={onAction} /> }] : []),
  ];
}
